import React, { useEffect, useMemo, useState, useRef } from "react";
import { useGetAppConfigurations, useUpdateAppConfigurations } from "../services/appconfiguration.service";
import { handleError, isNotNullUndefined } from "../helpers/common";
import "../assets/css/appconfiguration.css";
import { useAuth0 } from "@auth0/auth0-react";

type Path = (string | number)[];

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonArray;
interface JsonObject { [key: string]: JsonValue }
type JsonArray = JsonValue[];

function setValueAtPath(root: JsonValue, path: Path, value: JsonValue): JsonValue {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    if (Array.isArray(root)) {
        const clone = root.slice();
        clone[head as number] = setValueAtPath(clone[head as number], rest, value);
        return clone;
    }
    const obj = { ...(root as JsonObject) };
    obj[head as string] = setValueAtPath((root as JsonObject)?.[head as string], rest, value);
    return obj;
}

function coerceToType(input: string, current: JsonValue): JsonValue {
    if (typeof current === "number") {
        const n = input.trim() === "" ? NaN : Number(input);
        if (Number.isNaN(n)) return current; // keep old if invalid
        return n;
    }
    if (typeof current === "boolean") {
        // not used, boolean handled with checkbox
        return input.toLowerCase() === "true";
    }
    if (current === null) {
        // keep null when empty; otherwise treat as string
        return input === "" ? null : input;
    }
    return input; // string
}

// Helper function to check if a field should be treated as datetime
function isDateTimeField(path: Path): boolean {
    // Check if the field name suggests it's a date field
    const fieldName = path[path.length - 1] as string;
    const dateKeywords = ['date', 'time', 'datetime', 'created', 'updated', 'start', 'end'];
    return dateKeywords.some(keyword =>
        fieldName.toLowerCase().includes(keyword)
    );
};

// Helper function to format date for date input
function formatDateForInput(value: JsonValue): string {
    if (!value || value === null) return "";

    try {
        const date = new Date(String(value));
        if (isNaN(date.getTime())) return "";

        // Format as YYYY-MM-DD for date input
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch (error) {
        console.warn("Error formatting date:", error);
        return "";
    }
}

const Field: React.FC<{
    label: string;
    value: JsonValue;
    path: Path;
    onChange: (path: Path, v: JsonValue) => void;
    getDescription?: (path: Path) => string | null | undefined;
}> = ({ label, value, path, onChange, getDescription }) => {
    const id = useMemo(() => path.join("__"), [path]);
    const [isExpanded, setIsExpanded] = useState(false);
    const description = getDescription ? getDescription(path) : null;
    const displayLabel = description?.trim() || label;

    if (Array.isArray(value)) {
        return (
            <div className="config-section config-array">
                <div
                    className={`config-section-title ${!isExpanded ? 'collapsed' : ''}`}
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ cursor: 'pointer' }}
                >
                    {displayLabel} [ ]
                </div>
                {isExpanded && value.map((item, idx) => (
                    <Field key={idx} label={String(idx)} value={item} path={[...path, idx]} onChange={onChange} getDescription={getDescription} />
                ))}
            </div>
        );
    }
    if (value !== null && typeof value === "object") {
        return (
            <div className="config-section">
                <div
                    className={`config-section-title ${!isExpanded ? 'collapsed' : ''}`}
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ cursor: 'pointer' }}
                >
                    {displayLabel}
                </div>
                {isExpanded && Object.entries(value as JsonObject).map(([k, v]) => (
                    <Field key={k} label={k} value={v} path={[...path, k]} onChange={onChange} getDescription={getDescription} />
                ))}
            </div>
        );
    }

    // Primitive controls
    return (
        <div className="config-field">
            <label htmlFor={id} className="config-label">{displayLabel}</label>
            <div className="config-input-container">
                {typeof value === "boolean" ? (
                    <label className="checkbox-container">
                        <input
                            id={id}
                            type="checkbox"
                            checked={value}
                            onChange={(e) => onChange(path, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                    </label>
                ) : typeof value === "number" ? (
                    <input
                        id={id}
                        type="number"
                        className="config-input config-input-number"
                        value={String(value)}
                        onChange={(e) => onChange(path, coerceToType(e.target.value, value))}
                    />
                ) : isDateTimeField(path) ? (
                    <input
                        id={id}
                        type="date"
                        className="config-input config-input-date"
                        value={formatDateForInput(value)}
                        onChange={(e) => onChange(path, e.target.value)}
                    />
                ) : (
                    <input
                        id={id}
                        type="text"
                        className="config-input config-input-text"
                        value={value === null ? "" : String(value)}
                        placeholder={value === null ? "null" : undefined}
                        onChange={(e) => onChange(path, coerceToType(e.target.value, value))}
                    />
                )}
            </div>
        </div>
    );
};

const AppConfiguration = () => {
    const [configsByApp, setConfigsByApp] = useState<Map<string, JsonObject>>(new Map());
    const [descriptionMapsByApp, setDescriptionMapsByApp] = useState<Map<string, Map<string, string | null>>>(new Map());
    const [originalRowsByApp, setOriginalRowsByApp] = useState<Map<string, any[]>>(new Map());
    const [selectedApp, setSelectedApp] = useState<string | null>(null);
    const [appNames, setAppNames] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const hasInitialized = useRef(false);
    const { logout } = useAuth0();

    const getAppConfigurations = useGetAppConfigurations({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response) && response.length > 0) {
                try {
                    // Group rows by appName
                    const rowsByApp = new Map<string, any[]>();
                    for (const row of response) {
                        const appName = row.appName || "Unknown";
                        if (!rowsByApp.has(appName)) {
                            rowsByApp.set(appName, []);
                        }
                        rowsByApp.get(appName)!.push(row);
                    }

                    // Process each app's configurations
                    const configsMap = new Map<string, JsonObject>();
                    const descMapsMap = new Map<string, Map<string, string | null>>();
                    const originalRowsMap = new Map<string, any[]>();

                    rowsByApp.forEach((rows, appName) => {
                        const nested = rowsToNestedJson(rows);
                        const descMap = buildDescriptionMap(rows);
                        configsMap.set(appName, nested);
                        descMapsMap.set(appName, descMap);
                        originalRowsMap.set(appName, rows);
                    });

                    setConfigsByApp(configsMap);
                    setDescriptionMapsByApp(descMapsMap);
                    setOriginalRowsByApp(originalRowsMap);

                    // Set app names and select first app
                    const apps = Array.from(rowsByApp.keys());
                    setAppNames(apps);
                    if (apps.length > 0) {
                        setSelectedApp(apps[0]);
                    }
                } catch (e: any) {
                    handleError(e);
                } finally {
                    setLoading(false);
                    setRefreshing(false);
                }
            } else {
                alert("No configuration data found");
                setLoading(false);
                setRefreshing(false);
            }
        },
        onError: (error: any) => {
            if (error.message.includes("token is expired")) {
                try { window.localStorage.clear(); } catch { }
                logout({ logoutParams: { returnTo: window.location.origin } });
            }
            else {
                setLoading(false);
                setRefreshing(false);
                handleError(error);
            }
        },
    });

    const updateAppConfigurations = useUpdateAppConfigurations({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response))
                alert(response.statusMessage);
            else
                alert("Something went wrong.");
            setSaving(false);
        },
        onError: (error: any) => {
            handleError(error);
            setSaving(false);
        },
    });

    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        hasInitialized.current = true;
        console.log('🔄 Fetching app configurations...');
        getAppConfigurations.mutate(undefined);
    }, [getAppConfigurations]);

    const getDescription = useMemo(() => {
        return (path: Path): string | null | undefined => {
            if (!selectedApp) return null;
            const descMap = descriptionMapsByApp.get(selectedApp);
            if (!descMap) return null;
            const pathKey = path.join(".");
            return descMap.get(pathKey);
        };
    }, [selectedApp, descriptionMapsByApp]);

    const handleChange = (path: Path, v: JsonValue) => {
        if (!selectedApp) return;
        setConfigsByApp((prev) => {
            const newMap = new Map(prev);
            const currentConfig = newMap.get(selectedApp);
            if (currentConfig) {
                newMap.set(selectedApp, setValueAtPath(currentConfig, path, v) as JsonObject);
            }
            return newMap;
        });
    };

    const onClickSelectedApp = (selectedApp: string) => {
        setSelectedApp(selectedApp);
    };

    const handleSave = async () => {
        if (!selectedApp) return;
        const config = configsByApp.get(selectedApp);
        if (!config) return;
        try {
            setSaving(true);
            const originalRows = originalRowsByApp.get(selectedApp);
            if (!originalRows) throw new Error("No configuration rows loaded for selected app");
            const payload = nestedJsonToUpdatePayload(config, originalRows);
            console.log("Sending payload:", payload);
            updateAppConfigurations.mutate(payload);
        } catch (err: any) {
            alert(err.message ?? String(err));
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        console.log('🔄 Refreshing app configurations...');
        getAppConfigurations.mutate(undefined);
    };

    if (loading) return (
        <div className="config-container from-[#00B4D8] via-[#48CAB2] to-[#90ED8F]">
            <div className="config-loading">
                <div className="loading-spinner"></div>
                <p>Loading configuration…</p>
            </div>
        </div>
    );

    if (!selectedApp || !configsByApp.has(selectedApp)) return (
        <div className="config-container from-[#00B4D8] via-[#48CAB2] to-[#90ED8F]">
            <div className="config-error">
                <p>No configuration found.</p>
            </div>
        </div>
    );

    const currentConfig = configsByApp.get(selectedApp)!;

    return (
        <div className="config-container from-[#00B4D8] via-[#48CAB2] to-[#90ED8F]">
            <button
                className={`config-refresh-btn ${refreshing ? 'refreshing' : ''}`}
                onClick={handleRefresh}
                disabled={refreshing || loading}
                title="Refresh configurations"
            >
                {refreshing ? (
                    <>
                        <span className="btn-spinner"></span>
                        Refreshing...
                    </>
                ) : (
                    <>
                        <span className="btn-icon">↻</span>
                        Refresh
                    </>
                )}
            </button>
            <div className="config-header">
                <h1 className="config-title">
                    <span className="config-icon">⚙️</span>
                    Control Panel
                </h1>
                <p className="config-subtitle">Manage your application settings</p>
            </div>

            {appNames.length > 1 && (
                <div className="config-tabs">
                    {appNames.map((appName) => (
                        <button
                            key={appName}
                            className={`config-tab ${selectedApp === appName ? 'active' : ''}`}
                            onClick={() => onClickSelectedApp(appName)}
                        >
                            {selectedApp === appName && (
                                <span className="config-tab-checkmark">
                                    <svg className="checkmark-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path className="checkmark-path" d="M5 13l4 4L19 7" />
                                    </svg>
                                </span>
                            )}
                            {appName}
                        </button>
                    ))}
                </div>
            )}

            {appNames.length === 1 && selectedApp && (
                <div className="config-app-label">
                    <span className={`config-app-name ${selectedApp ? 'active' : ''}`}>
                        <span className="config-tab-checkmark">
                            <svg className="checkmark-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path className="checkmark-path" d="M5 13l4 4L19 7" />
                            </svg>
                        </span>
                        {selectedApp}
                    </span>
                </div>
            )}

            <div className="config-content">
                {Object.entries(currentConfig).map(([k, v]) => (
                    <Field key={`${selectedApp}-${k}`} label={k} value={v} path={[k]} onChange={handleChange} getDescription={getDescription} />
                ))}
            </div>

            <div className="config-actions">
                <button
                    className={`config-save-btn ${saving ? 'saving' : ''}`}
                    disabled={saving}
                    onClick={handleSave}
                >
                    {saving ? (
                        <>
                            <span className="btn-spinner"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <span className="btn-icon">💾</span>
                            Save Configuration
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default AppConfiguration;

// -------- helpers to transform flat rows <-> nested json --------

function buildDescriptionMap(rows: any[]): Map<string, string | null> {
    const map = new Map<string, string | null>();
    for (const row of rows) {
        const path = [...(row.namespace ? row.namespace.split(".") : []), row.key];
        const pathKey = path.join(".");
        map.set(pathKey, row.description || null);
    }
    return map;
}

function rowsToNestedJson(rows: any[]): JsonObject {
    const root: JsonObject = {};
    for (const row of rows) {
        const path = [...(row.namespace ? row.namespace.split(".") : []), row.key];
        let cursor: JsonObject = root;
        for (let i = 0; i < path.length - 1; i++) {
            const segment = path[i]!;
            if (cursor[segment] === undefined || cursor[segment] === null || typeof cursor[segment] !== "object" || Array.isArray(cursor[segment])) {
                cursor[segment] = {};
            }
            cursor = cursor[segment] as JsonObject;
        }
        const leaf = path[path.length - 1]!;
        cursor[leaf] = pickTypedValue(row);
    }
    return root;
}

function pickTypedValue(row: any): JsonPrimitive {
    switch (row.dataType) {
        case "bit":
            return !!row.valueBit;
        case "int":
            return Number(row.valueInt ?? 0);
        case "decimal":
            return Number(row.valueDecimal ?? 0);
        case "datetime":
            return row.valueDate ? String(row.valueDate) : "";
        case "string":
        default:
            return String(row.valueString ?? "");
    }
}

function nestedJsonToUpdatePayload(nested: JsonObject, rows: any[]): any[] {
    // Build a quick index by namespace+key for id lookup
    const index = new Map<string, any>();
    for (const r of rows) {
        index.set(`${r.namespace}||${r.key}`, r);
    }

    const payload: any[] = [];

    const walk = (obj: JsonValue, nsParts: string[]) => {
        if (obj !== null && typeof obj === "object") {
            if (Array.isArray(obj)) {
                obj.forEach((v, i) => walk(v, [...nsParts, String(i)]));
            } else {
                for (const [k, v] of Object.entries(obj as JsonObject)) {
                    walk(v, [...nsParts, k]);
                }
            }
            return;
        }
        // primitive leaf
        const key = nsParts[nsParts.length - 1]!;
        const namespace = nsParts.slice(0, -1).join(".");
        const row = index.get(`${namespace}||${key}`);
        if (!row) return;

        // Create entry with all fields required by stored procedure
        const entry: any = {
            appName: row.appName,
            environment: row.environment,
            namespace: row.namespace,
            key: row.key,
            dataType: row.dataType,
            valueString: null,
            valueInt: null,
            valueDecimal: null,
            valueBit: null,
            valueDate: null,
            valueJson: null,
            description: row.description || "",
            updatedBy: row.updatedBy || ""
        };

        // Set the appropriate value field based on dataType
        switch (row.dataType) {
            case "bit":
                entry.valueBit = Boolean(obj);
                break;
            case "int":
                entry.valueInt = Number(obj);
                break;
            case "decimal":
                entry.valueDecimal = Number(obj);
                break;
            case "datetime":
                entry.valueDate = obj ? String(obj) : null;
                break;
            case "string":
            default:
                entry.valueString = obj === null ? "" : String(obj);
                break;
        }
        payload.push(entry);
    };

    walk(nested, []);
    return payload;
}